import React, { useState, useEffect, useMemo } from 'react';
import api from 'services/api';
import { Link } from 'react-router-dom';
import { formatExpDate } from 'utils/formatter';
import { Row, Col } from 'react-bootstrap';
import Text from 'v3/components/Text';

import Select from 'v3/components/Select';

import { useTravel } from 'v3/pages/Travel';

import { FaExternalLinkAlt, FaCheckCircle, FaCircle } from 'react-icons/fa';

import { checkIfBlacklisted } from "../controller"
import { useSnackbar } from 'v3/components/Snackbar';

import theme from 'v3/theme';
import DriverAttachments from '../DriverAttachments';
import { WhatsAppIcon } from './styles';

function Driver({ errors, setDriver, driver, editing, onUpdateDriver }) {
  const linkTextStyle = {
    textDecoration: 'underline',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    width: 'fit-content',
  };
  const travel = useTravel();
  const snackbar = useSnackbar();

  const [paymentMethod, setPaymentMethod] = useState(null);
  const [driverAttachments, setDriverAttachments] = useState([]);

  function checkDriverPaymentMethod() {
    if (travel.data?.driverData) {
      setPaymentMethod(travel.data.driverData?.payment?.name);
    } else if (travel.data?.shipperProps) {
      setPaymentMethod(travel.data?.shipperProps?.payment?.name);
    }
  }

  useEffect(() => {
    checkDriverPaymentMethod();
  }, [travel.data]);

  useEffect(() => {
    async function fetchAttachments(driver_id) {
      const attachmentsDriver = await api.get(`driver/${driver_id}/attachments`);

      setDriverAttachments(attachmentsDriver);
    }

    if (travel.data.driverId) fetchAttachments(travel.data.driverId);
  }, [travel.data.driverId]);

  async function fetchDriver(search) {
    try {
      const response = await api.get('drivers/select', { params: { search } });
      return response.data;
    } catch (error) {
      return [];
    }
  }

  const driverPhone = useMemo(() => {
    const assertedPhone =
      travel.data.driver?.phoneCooperplace || travel.data.driver?.user?.phonenumber;

    let whatsappPhone = assertedPhone?.replace(/\D/g, '');

    const PHONE_LENGTH_WITHOUT_DDI = 13;
    if (whatsappPhone?.length <= PHONE_LENGTH_WITHOUT_DDI) {
      whatsappPhone = '55'.concat(whatsappPhone);
    }

    return {
      raw: assertedPhone,
      whatsapp: whatsappPhone,
    };
  }, [travel.data.driver]);

  useEffect(() => {
    setDriver(travel?.data?.driver);
  }, [travel?.data?.driver?.id]);

  async function onChangeDriver(value) {

    const onBlacklistedCallback = blacklist =>
      snackbar.show(
        <Col style={{ display: 'flex', flexDirection: 'column', gap: '0.5' }}>
          <Text>
            Não foi possível selecionar o motorista pois ele está bloqueado no blacklist
          </Text>
          {
            blacklist?.logs.length > 0 && (
              <Text>
                Descrição: {blacklist?.logs[0]?.description}
              </Text>
            )
          }
        </Col>,
        {
          type: 'error',
        }
      );
    const isBlackListed = await checkIfBlacklisted(
      value?.id,
      onBlacklistedCallback,
    );
    if (isBlackListed) return;

    setDriver(value);
    onUpdateDriver(value);
    travel.setData((prev) => ({
      ...prev,
      oldDriver: !!travel.data?.driver,
    }));
  }

  if (travel.isLoading) {
    return null;
  }

  const VerifiedDriver = () => {
    const verified = !!travel.data?.driverData?.verified_person;
    return (
      <Col lg={4} md={4} xs={4}>
        <Text type="regular" color="gray">
          {verified ? (
            <FaCheckCircle size={15} color={theme.colors.light_blue} />
          ) : (
            <FaCircle size={15} color={theme.colors.danger} />
          )}
          {verified ? ' Verificado' : ' Não Verificado'}
        </Text>
      </Col>
    );
  };

  const DriverAppInstall = () => {
    const driverUser = travel.data?.driver?.user;
    return (
      <Col lg={4} md={4} xs={4}>
        <Text type="regular" color="gray">
          {driverUser && driverUser.appUninstalled != null && driverUser.appUninstalled <= 2 ? (
            <FaCheckCircle size={15} color={theme.colors.success} />
          ) : driverUser && driverUser.appUninstalled > 2 ? (
            <FaCheckCircle size={15} color={theme.colors.warning} />
          ) : (
            <FaCircle size={15} color={theme.colors.danger} />
          )}
          {driverUser && driverUser.appUninstalled != null && driverUser.appUninstalled <= 2
            ? ' Ativo no APP'
            : driverUser && driverUser.appUninstalled > 2
            ? ' App inativo'
            : ' Inativo no APP'}
        </Text>
      </Col>
    );
  };

  return (
    <>
      {editing ? (
        <Row className="form">
          <Col xs={12} md={12} style={{ marginTop: '10px' }}>
            <Text color="grey" weight={500}>
              Motorista
            </Text>
          </Col>
          <Col xs={12}>
            <Select.Async
              onSearch={fetchDriver}
              value={driver}
              label="Motorista *"
              onChange={onChangeDriver}
              getOptionLabel={(option) => option?.socialName ?? option?.social_name}
              getOptionValue={(option) => option?.id}
              error={errors}
            />
          </Col>
        </Row>
      ) : (
        <Row>
          <Col style={{ display: 'flex', alignItems: 'flex-end', marginTop: '10px' }}>
            <Text color="dark" type="header" weight={500}>
              Motorista
            </Text>
            <VerifiedDriver />
            <DriverAppInstall />
          </Col>

          <Col xs={12}>
            <Link
              to={travel.data.driver?.socialName && `/motoristas/${travel.data?.driver?.id}`}
              target="_blank"
            >
              <Text type="regular" color="light_blue" as="p" className="mt-2" style={linkTextStyle}>
                {travel.data.driver?.socialName || 'Não informado'}
                <FaExternalLinkAlt color="#B5B5C3" size={12} />
              </Text>
            </Link>
          </Col>
        </Row>
      )}
      <Row>
        <Col xs={12} md={6}>
          <Text type="label" color="dark">
            {travel.data.driver?.isInternational ? 'Documento' : 'CPF'}
          </Text>
          <Text type="regular" color="gray" as="p" className="mt-2">
            {travel.data.driver?.cpfCnpj || 'Não informado'}
          </Text>
        </Col>

        <Col xs={12} md={6}>
          <Text type="label" color="dark">
            Telefone
          </Text>
          <a
            href={`https://api.whatsapp.com/send?l=pt-BR&phone=${driverPhone.whatsapp}`}
            target="_blank"
            rel="noreferrer"
          >
            <div>
              <WhatsAppIcon size={18} />
              <Text type="regular" color="gray">
                {driverPhone.raw || 'Não informado'}
              </Text>
            </div>
          </a>
        </Col>

        <Col xs={12} md={6}>
          <Text type="label" color="dark">
            CNH
          </Text>
          <Text type="regular" color="gray" as="p" className="mt-2">
            {travel.data.driver?.cnhNumber || 'Não informado'}
          </Text>
        </Col>
        {travel.data.personVerifications?.dueDate && (
          <Col xs={12} md={6}>
            <Text type="label" color="dark">
              Validado por
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {travel.data?.personVerifications?.verifiedByUser?.username || 'Não informado'}
            </Text>
          </Col>
        )}
        <Col xs={12} md={6}>
          <Text type="label" color="dark">
            Validade da habilitação
          </Text>
          <Text type="regular" color="gray" as="p" className="mt-2">
            {formatExpDate(travel.data.driver?.cnhExpDate)}
          </Text>
        </Col>
        <Col xs={12} md={6}>
          <Text type="label" color="dark">
            Tags
          </Text>
          <Text type="regular" color="gray" as="p" className="mt-2">
            {travel.data.driverData?.tags?.map((tag) => tag.name)?.join(', ') || 'Não informado'}
          </Text>
        </Col>
        <Col xs={12} md={12}>
          <Text type="label" color="dark">
            Método de pagamento
          </Text>
          <Text type="regular" color="gray" as="p" className="mt-2">
            {paymentMethod || 'Não informado'}
          </Text>
        </Col>
        <Col md={12}>
          <DriverAttachments driverAttachments={driverAttachments || { data: {} }} />
        </Col>
      </Row>
    </>
  );
}

export default Driver;
